'use es6';

import { jsx as _jsx } from "react/jsx-runtime";
import { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import CustomWidgetPropTypes from 'DesignData/propTypes/customWidgets';
import getComponentName from 'react-utils/getComponentName';
import * as CustomWidgetBufferActions from 'DesignData/actions/CustomWidgetBuffers';
import * as customWidgetBuffersSelectors from 'DesignData/selectors/customWidgetBuffers';
import * as cosIndexConnectionsSelectors from 'DesignData/selectors/cosIndexConnections';
import { dependentsByCategory as DependentsByCategoryPropType } from 'DesignData/propTypes/finder';
import { isBadRequestError } from 'DesignData/helpers/errorHelper';
import withSessionStorage from 'ContentComponents/cmv2/decorators/withSessionStorage';
import makeAsyncModal from 'DesignComponents/modals/makeAsyncModal';
import { compose } from 'redux';
const ForceUpgradeModal = makeAsyncModal(() => import(
/* webpackChunkName: "upgrade-modals" */
'DesignEditorsUI/components/modals/ForceUpgradeModal'));
const UpgradeModal = makeAsyncModal(() => import(
/* webpackChunkName: "upgrade-modals" */
'DesignEditorsUI/components/modals/UpgradeModal'));
export default function withUpgradeModals(WrappedComponent) {
  class HighOrderWithUpgradeModals extends Component {
    constructor(props) {
      super(props);

      this.handleUpgradeModalOpen = (customWidgetId, forceOpen = false) => {
        if (!this.props.getSessionStorageItem(customWidgetId) || forceOpen) {
          this.setState({
            isUpgradeModalOpen: true
          });
        }
      };

      this.handleUpgradeModalClose = () => {
        this.props.setSessionStorageItem(this.props.customWidgetId, true);
        this.setState({
          isUpgradeModalOpen: false
        });
      };

      this.handleUpgradeModalCallback = upgradeData => {
        this.handleUpgradeModalClose();

        if (upgradeData) {
          this.upgradeData = upgradeData;
          this.isUpgrading = true;
          this.props.upgradeCustomWidget(this.props.customWidgetId, upgradeData);
        }
      };

      this.handleForceUpgradeModalOpen = () => {
        this.setState({
          isForceUpgradeModalOpen: true
        });
      };

      this.handleForceUpgradeModalClose = () => {
        this.setState({
          isForceUpgradeModalOpen: false
        });
      };

      this.handleForceUpgradeModalCallback = confirmed => {
        this.handleForceUpgradeModalClose();

        if (confirmed) {
          this.props.forceUpgradeCustomWidget(this.props.customWidgetId, this.upgradeData || {});
        }
      };

      this.state = {
        isUpgradeModalOpen: false,
        isForceUpgradeModalOpen: false
      };
    }

    componentDidUpdate() {
      const {
        upgradeError: nextUpgradeError
      } = this.props;

      if (this.isUpgrading && nextUpgradeError && isBadRequestError(nextUpgradeError)) {
        this.isUpgrading = false;
        this.handleForceUpgradeModalOpen();
      }
    }

    renderUpgradeModal() {
      if (this.state.isUpgradeModalOpen) {
        return /*#__PURE__*/_jsx(UpgradeModal, {
          contentTypes: this.props.contentTypes,
          hostTemplateTypes: this.props.hostTemplateTypes,
          customWidgetId: this.props.customWidgetId,
          customWidgetName: this.props.customWidgetName,
          callback: this.handleUpgradeModalCallback,
          dependentsByCategory: this.props.dependentsByCategory,
          dependentsCount: this.props.dependentsCount
        });
      }

      return null;
    }

    renderForceUpgradeModal() {
      if (this.state.isForceUpgradeModalOpen) {
        return /*#__PURE__*/_jsx(ForceUpgradeModal, {
          customWidgetId: this.props.customWidgetId,
          callback: this.handleForceUpgradeModalCallback,
          upgradeError: this.props.upgradeError
        });
      }

      return null;
    }

    render() {
      return /*#__PURE__*/_jsx(WrappedComponent, Object.assign({}, this.props, {
        onUpgradeClick: (...partialArgs) => this.handleUpgradeModalOpen(this.props.customWidgetId, true, ...partialArgs),
        UpgradeModal: this.renderUpgradeModal(),
        ForceUpgradeModal: this.renderForceUpgradeModal()
      }));
    }

  }

  HighOrderWithUpgradeModals.propTypes = {
    customWidgetId: CustomWidgetPropTypes.customWidgetId.isRequired,
    // @connect
    customWidgetName: PropTypes.string.isRequired,
    upgradeError: PropTypes.object,
    upgradeCustomWidget: PropTypes.func.isRequired,
    forceUpgradeCustomWidget: PropTypes.func.isRequired,
    dependentsByCategory: DependentsByCategoryPropType.isRequired,
    contentTypes: PropTypes.array.isRequired,
    hostTemplateTypes: PropTypes.array.isRequired,
    dependentsCount: PropTypes.number,
    setSessionStorageItem: PropTypes.func.isRequired,
    getSessionStorageItem: PropTypes.func.isRequired
  };

  function makeMapStateToProps() {
    const getRequestStatus = customWidgetBuffersSelectors.makeGetRequestStatus();
    const getCustomWidgetBuffer = customWidgetBuffersSelectors.makeGetCustomWidgetBuffer();
    const getDependentsByCategory = cosIndexConnectionsSelectors.makeGetParentConnectionsByCategory();
    const getDependentsCount = cosIndexConnectionsSelectors.makeGetParentConnectionsCount();
    return (state, props) => {
      const {
        upgradeError
      } = getRequestStatus(state, props);
      const {
        name = '',
        hostTemplateTypes = [],
        contentTypes = []
      } = getCustomWidgetBuffer(state, props) || {};
      return {
        hostTemplateTypes,
        contentTypes,
        customWidgetName: name,
        upgradeError,
        dependentsCount: getDependentsCount(state, {
          assetId: props.customWidgetId
        }),
        dependentsByCategory: getDependentsByCategory(state, {
          assetId: props.customWidgetId
        })
      };
    };
  }

  const mapDispatchToProps = {
    upgradeCustomWidget: CustomWidgetBufferActions.upgrade,
    forceUpgradeCustomWidget: CustomWidgetBufferActions.forceUpgrade
  };
  return compose(connect(makeMapStateToProps, mapDispatchToProps), withSessionStorage(getComponentName(withUpgradeModals)))(HighOrderWithUpgradeModals);
}